import { jsxs, jsx } from "react/jsx-runtime";
import { styled, Stack, GlobalStyles } from "@mui/material";
import cn from "../../../external/classnames/index.js";
import { useState, useMemo } from "react";
import ReactPhoneNumberInput, { parsePhoneNumber, formatPhoneNumberIntl } from "../../../external/react-phone-number-input/min/index.js";
import cssStyle from "../../../external/react-phone-number-input/style.css.js";
import { sizeStyles, fieldSizeMap } from "../../common/Common.js";
import { TimesButton } from "../../common/TimesButton.js";
import { inputContainerStyles } from "../../common/styles.js";
import SvgArrowLg from "./images/arrowLg.svg.js";
import SvgArrowMd from "./images/arrowMd.svg.js";
import SvgArrowSm from "./images/arrowSm.svg.js";
import { useInternalControlled } from "../../../hooks/useInternalControlled.js";
import { createEvent } from "../../../utils/createEvent.js";
import { DATA_TEST_ID_INHERITED } from "../../../constants/DATA_TEST_ID_INHERITED.js";
import { CLEAR_BUTTON_CLASSNAME } from "../../InputControl/Input/InputBase.js";
import { useFocusClassName } from "./useFocusClassName.js";
const StyledReactPhoneNumberInput = styled(ReactPhoneNumberInput)({}), countrySelector = ".PhoneInputCountry", arrowIconContainerSelector = ".PhoneInputCountrySelectArrow", phoneInputCountryIconSelector = ".PhoneInputCountryIcon", arrowsMap = {
  sm: SvgArrowSm,
  md: SvgArrowMd,
  lg: SvgArrowLg
}, arrowIconStyles = {
  sm: { marginLeft: 8, paddingBottom: 2 },
  md: { marginLeft: 19 },
  lg: { marginLeft: 14 }
}, countrySelectorStyles = {
  sm: { paddingLeft: 7 },
  md: { minWidth: 82, paddingLeft: 12 },
  lg: { minWidth: 82, paddingLeft: 12 }
}, PhoneInput = ({
  onChange,
  onBlur,
  value,
  id,
  name,
  className,
  disabled,
  size = "md",
  defaultCountry = "US",
  error,
  autoComplete,
  endAdornment,
  endAdornmentPadding,
  ...restProps
}) => {
  const [country, setCountry] = useState(defaultCountry), { internalValue, handleChange } = useInternalControlled({
    onChange,
    value,
    forced: !0
  }), { targetRef } = useFocusClassName("Mui-focused"), hasContent = !!(internalValue != null && internalValue.length), allStyles = useMemo(() => [
    {
      flex: "1 1 auto",
      [arrowIconContainerSelector]: arrowIconStyles[size],
      // todo [tech-debt]: move all styles to the styles.ts
      [countrySelector]: countrySelectorStyles[size],
      [phoneInputCountryIconSelector]: { width: "21px" },
      input: {
        ...sizeStyles.inputStyles[size],
        ...hasContent ? sizeStyles.inputFilledStyles[size] : void 0
      }
    }
  ], [hasContent, size]), handleClear = () => {
    handleChange == null || handleChange(createEvent({ name, value: "" }));
  }, onChangeInternal = (value2) => {
    handleChange == null || handleChange(createEvent({ value: value2 ?? "", name }));
  }, autoCompleteProp = autoComplete === !1 ? "new-password" : autoComplete, endAdornmentContent = endAdornment || (hasContent ? /* @__PURE__ */ jsx(
    TimesButton,
    {
      "data-test-id": DATA_TEST_ID_INHERITED,
      size,
      onClick: handleClear
    }
  ) : void 0);
  return /* @__PURE__ */ jsxs(
    Stack,
    {
      ref: targetRef,
      sx: [
        ...styles.inputContainer,
        {
          height: fieldSizeMap[size]
        }
      ],
      className: cn(
        className,
        disabled ? "field-disabled" : void 0,
        error ? "field-error" : void 0
      ),
      children: [
        /* @__PURE__ */ jsx(GlobalStyles, { styles: cssStyle }),
        /* @__PURE__ */ jsx(
          StyledReactPhoneNumberInput,
          {
            "data-test-id": DATA_TEST_ID_INHERITED,
            sx: allStyles,
            name,
            country,
            defaultCountry: country,
            disabled,
            onChange: onChangeInternal,
            onInput: (e) => {
              const index = e.target.value.lastIndexOf("+"), value2 = index !== -1 ? e.target.value.slice(index) : e.target.value;
              let parsed = parsePhoneNumber(value2, country);
              if (parsed || (parsed = parsePhoneNumber(value2.slice(1))), (parsed == null ? void 0 : parsed.country) === country) {
                const formatted = formatPhoneNumberIntl(parsed.number);
                window.setTimeout(() => {
                  onChangeInternal(formatted);
                });
              }
            },
            onBlur,
            international: !0,
            withCountryCallingCode: !0,
            countryCallingCodeEditable: !1,
            onCountryChange: (ctr) => {
              ctr && setCountry(ctr);
            },
            value: internalValue ?? void 0,
            id,
            countrySelectProps: {
              arrowComponent: () => {
                const Icon = arrowsMap[size];
                return /* @__PURE__ */ jsx(Icon, { className: "PhoneInputCountrySelectArrow" });
              },
              autoComplete: "new-password"
            },
            autoComplete: autoCompleteProp,
            ...restProps
          }
        ),
        endAdornmentContent ? /* @__PURE__ */ jsx(
          Stack,
          {
            sx: [
              styles.endAdornmentContainer,
              endAdornmentPadding ? { paddingInlineEnd: 12 } : {}
            ],
            children: endAdornmentContent
          }
        ) : null
      ]
    }
  );
}, d = inputContainerStyles({
  inputBodySelector: ".PhoneInput",
  inputFocusedSelector: ".PhoneInput--focus",
  disabledSelector: ".field-disabled",
  errorSelector: ".field-error",
  inputSelector: "input"
}), styles = {
  endAdornmentContainer: {
    height: "100%",
    justifyContent: "center",
    gap: 12,
    flexDirection: "row",
    alignItems: "center"
  },
  inputContainer: [
    d,
    {
      flexDirection: "row",
      alignItems: "center"
    },
    {
      "&.Mui-focused": {
        [`.${CLEAR_BUTTON_CLASSNAME}`]: {
          visibility: "visible"
        }
      }
    },
    {
      "&.field-disabled": {
        [countrySelector]: (t) => ({
          backgroundColor: "transparent",
          borderRight: `1px solid ${t.palette.field.regularbg.border.disabled}`
        }),
        img: {
          filter: "grayscale(1)",
          opacity: 0.4
        }
      },
      [countrySelector]: (t) => ({
        borderRight: `1px solid ${t.palette.field.regularbg.border.default}`,
        margin: 0,
        padding: 3,
        minWidth: 62,
        boxSizing: "border-box"
      }),
      ".PhoneInput:hover": {
        [countrySelector]: (t) => ({
          borderColor: t.palette.field.regularbg.border.hover
        })
      },
      ".PhoneInput--focus": {
        [countrySelector]: (t) => ({
          borderColor: t.palette.field.regularbg.border.active,
          borderWidth: 2
        })
      },
      "&.field-error.field-error.field-error, &.field-error .PhoneInput--focus": {
        [countrySelector]: (t) => ({
          borderColor: `${t.palette.field.regularbg.border.error}!important`,
          borderWidth: 2
        })
      },
      [arrowIconContainerSelector]: {
        transform: "none",
        border: 0,
        width: "initial",
        height: "initial",
        opacity: 1
      },
      [phoneInputCountryIconSelector]: {
        height: 18
      },
      ".PhoneInputCountryIcon--border": { boxShadow: "none" }
    }
  ]
};
export {
  PhoneInput,
  StyledReactPhoneNumberInput
};
//# sourceMappingURL=PhoneInput.js.map
